import { useMemo } from "react";
import { Control, useFormContext } from "react-hook-form";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ArrowRight, ExternalLink, Loader2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { KnowledgeBaseSpotSelectField } from "@/components/KnowledgeBases/Select/KnowledgeBasesDropdown";
import { KnowledgeBasesV2Service, KnowledgeBaseV2 } from "@/generated";
import { formatTime, timeAgo } from "@/utils/generic";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { Banner } from "@/components/Banner";
import { Button } from "@/components/shadcn/button";
import { Skeleton } from "@/components/shadcn/skeleton";

export const KBPickerField = ({
  name,
  control,
  label,
  isDisabled,
}: {
  name: string;
  control: Control;
  label: string;
  isDisabled?: boolean;
}) => {
  const { spotGroup } = useSpotUser();
  const form = useFormContext();

  const { data: knowledgeBasesData, isLoading } = useInfiniteQuery({
    queryKey: [DATA_KEYS.KNOWLEDGE_BASES_V2, ""],
    queryFn: ({ pageParam = 1 }) =>
      KnowledgeBasesV2Service.getKnowledgeBasesV2({
        path: { group_id: spotGroup.id },
        query: { page: pageParam, size: 50 },
      }),
    staleTime: 3000,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.data || !lastPage.data.page || !lastPage.data.pages)
        return undefined;
      return lastPage.data.page < lastPage.data.pages
        ? allPages.length + 1
        : undefined;
    },
    initialPageParam: 1,
  });

  const selectedKnowledgeBaseId = form.watch(name);

  const selectedKnowledgeBase = useMemo(() => {
    return knowledgeBasesData?.pages
      ?.flatMap((page) => page.data.items)
      .find((kb) => kb.id === selectedKnowledgeBaseId);
  }, [knowledgeBasesData, selectedKnowledgeBaseId]);

  if (!knowledgeBasesData) {
    return (
      <div>
        <Skeleton className="h-[1.375rem] w-[40%] mb-1" />
        <Skeleton className="h-[2.5rem] mb-4" />
        <Skeleton className="h-[9rem]" />
      </div>
    );
  }

  return (
    <div>
      <KnowledgeBaseSpotSelectField
        label={label}
        name={name}
        control={control}
        placeholder="Pick one option"
        options={[
          ...(knowledgeBasesData?.pages
            ?.flatMap((page) => page.data.items)
            .map((kb) => ({
              value: kb.id,
              label: kb.name,
              description: `Last updated: ${timeAgo(kb.updated_at)}`,
              sources: kb.num_docs > 0 ? ["document"] : [],
              type: kb.kb_type,
            })) || []),
        ]}
        isLoading={isLoading}
        isDisabled={isDisabled}
      />

      {!selectedKnowledgeBase && (
        <div className="mt-4">
          <NoKnowledgeBaseSelected />
        </div>
      )}

      {selectedKnowledgeBase && (
        <div className="mt-4">
          <KnowledgeBaseDetails kb={selectedKnowledgeBase} />
        </div>
      )}
    </div>
  );
};

const NoKnowledgeBaseSelected = () => {
  const goToKnowledgeBases = () => {
    window.open(`/?tab=knowledge-bases`, "_blank");
  };

  return (
    <div className="py-4 px-10 flex items-center justify-between border border-border rounded-xl shadow-sm">
      <div className="flex justify-start items-center mr-10 shrink-0">
        <Image
          src="https://storage.googleapis.com/image-storage-spot-manual/kbs/folder-drawing.svg"
          alt="No Knowledge Base selected"
          width={90}
          height={57}
        />
      </div>
      <div>
        <p className="text-sm font-semibold">No knowledge base selected</p>
        <p className="text-xs text-muted-foreground">
          Select a knowledge base to use or go to Knowledge Bases to create one.
        </p>
        <Button className="mt-4" onClick={goToKnowledgeBases}>
          <ArrowRight strokeWidth="1.5px" size="1.25rem" />
          Go to Knowledge Bases
        </Button>
      </div>
    </div>
  );
};

const KnowledgeBaseDetails = ({ kb }: { kb: KnowledgeBaseV2 }) => {
  const { spotGroup } = useSpotUser();
  const { data } = useRemoteData(
    DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
    {
      path: {
        group_id: spotGroup.id,
        knowledge_base_id: kb.id,
      },
    },
    {
      refetchInterval: kb.status === "training" ? 2500 : false,
    }
  );

  const highlights = [
    { label: "Last updated", value: timeAgo(kb.updated_at) },
    {
      label: "Added at",
      value: formatTime(kb.created_at, { dateStyle: "short" }),
    },
    { label: "Total sources", value: kb.num_docs },
  ];

  const allDocumentsErrored =
    data &&
    data.error_documents === data.total_documents &&
    data.total_documents > 0;

  const hasErroredDocs =
    kb.status === "trained" && data && data?.error_documents > 0;

  const bannerStatus = allDocumentsErrored
    ? "error"
    : hasErroredDocs
      ? "warning"
      : "info";

  const bannerLabel =
    data?.total_documents === 0
      ? "No items uploaded"
      : allDocumentsErrored
        ? "There was an error training your knowledge base."
        : hasErroredDocs
          ? "Some items were not processed."
          : "Processing some items";

  const showBanner =
    hasErroredDocs ||
    allDocumentsErrored ||
    kb.status === "training" ||
    kb.status === "created" ||
    kb.status === "error" ||
    (data && data.total_documents === 0);

  return (
    <div className="p-4 border border-border rounded-xl">
      <div className="flex flex-col items-stretch w-full gap-3">
        <div className="flex items-center gap-2 justify-between">
          <p className="text-sm font-semibold line-clamp-1">{kb.name}</p>
          <Button variant="outline" asChild>
            <Link
              href={`/data-hub/${kb.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size="1rem" />
              Manage files
            </Link>
          </Button>
        </div>

        {showBanner && (
          <div className="my-2">
            <Banner
              label={
                <div className="flex justify-between items-center w-full">
                  <p className="text-sm">{bannerLabel}</p>
                  <div className="flex gap-2 ml-1">
                    {data &&
                      (kb.status === "training" || kb.status === "created") &&
                      data.total_documents > 0 &&
                      !allDocumentsErrored && (
                        <>
                          <p className="text-xs">
                            {data.trained_documents + data.error_documents} /
                            {data.total_documents}
                          </p>
                          <Loader2 className="size-4 animate-spin" />
                        </>
                      )}
                  </div>
                </div>
              }
              style={bannerStatus}
              size="sm"
            />
          </div>
        )}

        <div className="flex justify-between">
          {highlights.map((highlight) => (
            <div
              key={highlight.label}
              className="flex flex-col items-start text-left gap-0 flex-grow"
            >
              <p className="text-xs font-semibold text-muted-foreground line-clamp-1">
                {highlight.label}
              </p>
              <p className="text-sm line-clamp-1">{highlight.value}</p>
            </div>
          ))}
        </div>
        <div>
          <p className="text-xs font-semibold text-muted-foreground">
            Knowledge base description
          </p>
          <p className="text-sm">{kb.description}</p>
        </div>
      </div>
    </div>
  );
};
